import { useContext, useEffect, useState } from 'react';
import { Card, CardBody } from 'reactstrap';
import { Uploading } from '~/shared/components/Loading';
import Dropzone, { type Accept } from 'react-dropzone';
import imageCompression from 'browser-image-compression';
import {
    fetchFeatureImagePresignedUrl,
    flyerImagePresignedUrl,
    uploadFile,
} from '~/services/campaign';
import { CampaignContext } from '~/context/campaign';
import { useToasts } from 'react-toast-notifications';
import { UseFormSetValue } from 'react-hook-form';
import { renderIcon } from '~/shared/components/icons';
import { getFilename } from '~/services/helper';
import { normalizeFileName } from '~/shared/utils';

const resizeImage = async (file: File) => {
    const options = {
        maxSizeMB: 2,
        maxWidthOrHeight: 2000,
        useWebWorker: true,
    };
    const compressedFile = await imageCompression(file, options);
    return compressedFile;
};

type Props = {
    setValue: UseFormSetValue<any>;
    type: 'event_picture' | 'flyer';
    accept: Accept;
    savedUrl?: string;
};

export const FundraiserImageUpload = ({
    setValue,
    type,
    accept,
    savedUrl,
}: Props) => {
    const { addToast } = useToasts();
    const [savingImage, setSavingImage] = useState(false);
    const [imgUrl, setImgUrl] = useState(savedUrl);
    const [isDisabled, setIsDisabled] = useState(false);
    const [{ campaign }] = useContext(CampaignContext);

    const ImgDropzone = () => {
        const handleDrop = async (files: File[]) => {
            const campaignId = campaign.campaignDetail.campaign_id;
            setSavingImage(true);

            try {
                for (const file of files) {
                    const fileName = normalizeFileName(file);
                    const { fields, url } =
                        type === 'event_picture'
                            ? await fetchFeatureImagePresignedUrl(
                                  fileName,
                                  campaignId
                              )
                            : await flyerImagePresignedUrl(
                                  fileName,
                                  campaignId
                              );

                    if (type === 'event_picture') {
                        const resized = await resizeImage(file);
                        await uploadFile(fields, url, resized);
                    } else {
                        await uploadFile(fields, url, file);
                    }

                    const fullUrl = url + fields.key;
                    setValue(type, fullUrl);
                    setImgUrl(fullUrl);
                }
            } catch (e) {
                addToast('Something went wrong!');
            } finally {
                setSavingImage(false);
            }
        };

        const dropzoneMessage = () => {
            return (
                <div className='campaign-photo-dropzone text-center'>
                    {savingImage ? (
                        <Uploading />
                    ) : (
                        <div>
                            {imgUrl || savedUrl ? (
                                <div
                                    className='position-relative mx-auto'
                                    style={{ width: '220px' }}
                                >
                                    <img
                                        src={imgUrl ? imgUrl : savedUrl}
                                        alt={''}
                                        style={{
                                            height: '150px',
                                            width: '150px',
                                            objectFit: 'cover',
                                        }}
                                    />
                                    <div className='small my-2'>
                                        {getFilename(
                                            imgUrl ? imgUrl : savedUrl
                                        )}
                                    </div>
                                    <button
                                        className='text-danger fs-4 btn btn-link p-0 position-absolute top-0 end-0'
                                        onClick={() => {
                                            setValue('event_picture', '');
                                            setImgUrl('');
                                            setIsDisabled(false);
                                        }}
                                    >
                                        {renderIcon('close')}
                                    </button>
                                </div>
                            ) : (
                                <div className='text-primary fs-4'>
                                    {renderIcon('upload')}
                                </div>
                            )}
                        </div>
                    )}

                    <p className='fs-6'>
                        <strong>Click to upload</strong> or drag and drop
                        <br /> PNG, JPG, or GIF (max 2000 x 2000px)
                    </p>
                </div>
            );
        };

        useEffect(() => {
            if (savedUrl) {
                // setImgUrl(savedUrl);
                setIsDisabled(true);
            }
        }, [imgUrl]);

        return (
            <Dropzone
                onDrop={handleDrop}
                accept={accept}
                multiple={false}
                disabled={isDisabled}
            >
                {({ getRootProps, getInputProps }) => (
                    <Card
                        className='my-3'
                        style={{
                            border: '2px dashed #949599',
                            background: '#F0F6F6',
                        }}
                    >
                        <CardBody>
                            <div {...getRootProps()}>
                                <input {...getInputProps()} />
                                {dropzoneMessage()}
                            </div>
                        </CardBody>
                    </Card>
                )}
            </Dropzone>
        );
    };

    return <ImgDropzone />;
};
